/**
 * Practical email validator.
 *
 * Goals:
 * - Catch obvious invalid input (spaces, missing @, missing dot in domain)
 * - Avoid being overly strict (supports + tags, subdomains)
 * - Provide a normalized value for consistency (trim + lowercase)
 */

/**
 * @param {string} value
 * @returns {{ ok: true, normalized: string } | { ok: false, message: string }}
 */
export function validateEmail(value) {
  const raw = String(value ?? '');
  const normalized = raw.trim().toLowerCase();

  if (!normalized) {
    return { ok: false, message: 'Please enter your email address.' };
  }

  if (/\s/.test(normalized)) {
    return { ok: false, message: "Email addresses can't contain spaces." };
  }

  // Basic structure: local@domain
  const at = normalized.indexOf('@');
  if (at <= 0 || at !== normalized.lastIndexOf('@')) {
    return { ok: false, message: 'Please enter a valid email address.' };
  }

  const local = normalized.slice(0, at);
  const domain = normalized.slice(at + 1);

  if (!local || !domain) {
    return { ok: false, message: 'Please enter a valid email address.' };
  }

  // Prevent obvious bad domains.
  if (!domain.includes('.')) {
    return {
      ok: false,
      message: 'Email domain looks incomplete (example: name@domain.com).',
    };
  }

  // No consecutive dots.
  if (local.includes('..') || domain.includes('..')) {
    return { ok: false, message: 'Please enter a valid email address.' };
  }

  // Practical character set.
  // We intentionally keep it narrower than full RFC 5322 to avoid accepting
  // surprising addresses (e.g. containing '~' or other rarely-used symbols).
  //
  // Allowed local part chars: letters, digits, dot, underscore, hyphen,
  // plus, and apostrophe.
  // Allowed domain chars: letters, digits, hyphen + dots between labels.
  const practical = /^[a-z0-9._+'-]+@[a-z0-9-]+(\.[a-z0-9-]+)+$/i;
  if (!practical.test(normalized)) {
    return { ok: false, message: 'Please enter a valid email address.' };
  }

  // Length sanity checks (avoid sending obviously too-long values).
  if (normalized.length > 254) {
    return { ok: false, message: 'Email address is too long.' };
  }
  if (local.length > 64) {
    return { ok: false, message: 'Email username part is too long.' };
  }

  return { ok: true, normalized };
}

/**
 * Password strength validator (must match backend logic).
 * Backend regex:
 * ^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$
 */

export const PASSWORD_REQUIREMENTS_TEXT =
  'Password must be at least 8 characters and include: 1 uppercase, 1 lowercase, 1 number, and 1 special character (@ $ ! % * ? &).';

/**
 * @param {string} value
 * @returns {{ ok: true } | { ok: false, message: string }}
 */
export function validatePasswordStrength(value) {
  const password = String(value ?? '');
  if (!password) {
    return { ok: false, message: 'Please enter a password.' };
  }

  const strong =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
  if (!strong.test(password)) {
    return { ok: false, message: PASSWORD_REQUIREMENTS_TEXT };
  }
  return { ok: true };
}
